import { HTMLAttributes, memo, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { BULK_POPOVER_ATTR } from '@ardoq/popovers';
import { UnstyledButton } from '@ardoq/button';
import { colors } from '@ardoq/design-tokens';
import { classes } from '@ardoq/common-helpers';
import {
  ACTIVE_BOX_SHADOW,
  ACTIVE_BRIGHTNESS,
  EXPORT_AUTOSIZE_CLASS_NAME,
} from '@ardoq/global-consts';
import {
  ComponentLabel,
  OtherLabelBadge as OtherLabel,
  UrlFieldValueIcon,
  getNodeUrlFieldValues,
  maybeDispatchUrlIconClick,
} from '@ardoq/graph';
import {
  COLLAPSED_REFERENCE_BOX_SHADOW,
  COLLAPSED_REFERENCE_BOX_SHADOW_WIDTH,
  FLEX_GAP,
  GLOBAL_HANDLER_ID_ATTRIBUTE,
  REFERENCE_BORDER_RADIUS,
  URL_FIELD_VALUES_ATTR,
  VIEW_NODE_ID_ATTR,
} from './consts';
import {
  DependencyMapNodeViewModel,
  DependencyMapViewNodeProperties,
} from './types';
import {
  hasComponentId,
  haveSameCallbacks,
  isNodeEffectivelyEqual,
  nodeHasComponentIdInReferences,
} from './viewComponentEquivalence';
import { ReferenceDirection, ViewIds } from '@ardoq/api-types';
import { useResizeObserver } from '@ardoq/hooks';
import { DependencyMapReferenceLabel } from './view/DependencyMapReferenceLabel';
import { DependencyMapHeaderLabel } from './view/DependencyMapHeaderLabel';
import { FlexBox } from '@ardoq/layout';
import { isEqual } from 'lodash';

const OtherLabelsContainer = styled(FlexBox).attrs({
  gap: 'xsmall',
  flexWrap: true,
})`
  min-height: fit-content;
`;

type ComponentNameContainerProperties = {
  $isWidthSmallerThanVisible: boolean;
  $textColor?: string;
};
const ComponentNameContainer = styled.div<ComponentNameContainerProperties>`
  ${({ $isWidthSmallerThanVisible }) =>
    !$isWidthSmallerThanVisible &&
    css`
      pointer-events: none;
    `}
  position: relative;
  color: ${({ $textColor }) => $textColor ?? colors.textModerate};
  display: flex;
  line-height: 18px;
  align-items: center;
  margin: 4px 0;
  & .component-name {
    flex: 1 0;
    color: ${({ $textColor }) => $textColor ?? colors.textModerate};
  }
`;
type ContainerArgs = {
  $hasContent: boolean;
  $isRoot: boolean;
  $showVertical: boolean;
};

const Container = styled.div<ContainerArgs>`
  position: relative;
  padding: ${({ $hasContent }) => ($hasContent ? 16 : 8)}px 16px;
  flex-grow: ${({ $isRoot }) => ($isRoot ? undefined : 1)};
  max-width: 100%;
  user-select: none;
  ${({ $showVertical, $isRoot }) =>
    !$showVertical && $isRoot && 'height: fit-content;'}
`;

interface ColorBoxArgs {
  $degreeOfParenthood: number;
  $isReference: boolean;
  $isCollapsedReference: boolean;
  $containsCollapsedNodes: boolean;
  $isActive: boolean;
  $isSelected: boolean;
}
const ColorBox = styled.div<ColorBoxArgs>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${colors.surfaceDefaultSubtle};
  pointer-events: none;
  box-shadow: ${({ $isReference, $isCollapsedReference }) =>
    $isReference
      ? $isCollapsedReference
        ? COLLAPSED_REFERENCE_BOX_SHADOW
        : 'none'
      : '0 0 4px color-mix(in srgb, 21% rgba(0, 0, 0, 1), 79% transparent)'};
  border-width: 1px;
  border-radius: ${({ $isReference }) =>
    $isReference ? REFERENCE_BORDER_RADIUS : '2'}px;
  transition-property: filter, box-shadow;
  transition-duration: 300ms;
  transition-timing-function: ease-in-out;
  ${({ $degreeOfParenthood, $containsCollapsedNodes }) =>
    $degreeOfParenthood
      ? css`
          border-width: 2px;
          border-style: dotted;
        `
      : $containsCollapsedNodes
        ? css`
            border-width: 3px;
            border-style: double;
          `
        : css`
            border-width: 1px;
            border-style: solid;
          `}
  ${({ $isActive, $isSelected, $isCollapsedReference }) =>
    ($isActive || $isSelected) &&
    css`
      filter: brightness(${ACTIVE_BRIGHTNESS});
      ${!$isCollapsedReference ? `box-shadow: ${ACTIVE_BOX_SHADOW};` : ''}
    `}
`;

const SubLabel = styled(DependencyMapHeaderLabel)`
  font-style: italic;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  margin-bottom: 3px;
  overflow-wrap: break-word;
`;

const ItemsContainer = styled.div<{ $showVertical: boolean }>`
  display: flex;
  max-width: 100%;
  justify-content: flex-start;
  gap: ${FLEX_GAP}px;
  margin-top: 12px;
  flex-wrap: wrap;
  flex-direction: ${({ $showVertical }) => ($showVertical ? 'row' : undefined)};
  overflow: clip;
  overflow-clip-margin: ${COLLAPSED_REFERENCE_BOX_SHADOW_WIDTH}px;
`;

const MIN_VISIBLE_WITHOUT_POPUP_WIDTH = 100;

const DependencyMapViewNodeBase = ({
  viewInstanceId,
  node,
  referenceDirection,
  headerLabel,
  headerSubLabels,
  subLabel,
  colorByReference,
  className,
  selectedComponentId,
  hoveredComponentId,
  focusedComponentId,
  setHoveredComponentId,
  setFocusedComponentId,
  showVertical,
  isRoot,
  isCollapsedReference,
  onComponentClick,
  onReferenceClick,
  onComponentDoubleClick,
  urlFieldValuesByComponentId,
  urlFieldValuesByReferenceId,
  referenceIds,
  iconColor,
  fill,
  stroke,
  textColor: propsTextColor,
  textModerateColor: propsTextModerateColor,
  siblingCount,
  contextMenuDataAttribute,
  isNewConditionalFormatting,
  DependencyMap,
}: DependencyMapViewNodeProperties &
  Pick<HTMLAttributes<HTMLDivElement>, 'className'>) => {
  const {
    modelId,
    label,
    otherLabels,
    isComponent,
    representationData,
    degreeOfParenthood,
    degreeOfChildhood,
    containsCollapsedNodes,
    textColor: nodeTextColor,
    textModerateColor: nodeTextModerateColor,
  } = node;
  const textColor = propsTextColor ?? nodeTextColor;
  const textModerateColor = propsTextModerateColor ?? nodeTextModerateColor;
  const isReference = referenceDirection !== ReferenceDirection.NONE;
  const hasContent = Boolean(
    node.children?.length ||
    node.incomingReferences?.length ||
    node.outgoingReferences?.length ||
    node.combinedReferences?.length
  );

  const width =
    showVertical && isRoot ? (siblingCount < 3 ? 420 : 280) : undefined;
  const contextMenuAttribute = contextMenuDataAttribute
    ? { [contextMenuDataAttribute]: modelId }
    : {};

  const containerRef = useRef<HTMLDivElement>(null);
  const { width: dynamicWidth = 0 } = useResizeObserver(containerRef);
  const isWidthSmallerThanVisible =
    dynamicWidth <= MIN_VISIBLE_WITHOUT_POPUP_WIDTH;

  const isActive =
    hoveredComponentId === modelId || focusedComponentId === modelId;
  const isSelected = selectedComponentId === modelId;

  const labelId = `dep-map-label-${node.id}`;
  const referenceDescId = `dep-map-ref-${node.id}`;

  const urlFieldValues = useMemo(
    () =>
      getNodeUrlFieldValues(
        modelId,
        referenceIds,
        urlFieldValuesByComponentId,
        urlFieldValuesByReferenceId
      ),
    [
      modelId,
      referenceIds,
      urlFieldValuesByComponentId,
      urlFieldValuesByReferenceId,
    ]
  );

  return (
    <Container
      role="treeitem"
      aria-selected={isSelected || isActive}
      aria-labelledby={labelId}
      aria-describedby={referenceDescId}
      {...{
        [GLOBAL_HANDLER_ID_ATTRIBUTE]: modelId,
        ...contextMenuAttribute,
      }}
      data-reference-model-ids={referenceIds?.join(',') ?? undefined}
      style={{
        width,
      }}
      className={classes(
        isComponent && 'component',
        EXPORT_AUTOSIZE_CLASS_NAME
      )}
      onMouseOver={e => {
        if (isComponent) {
          setHoveredComponentId(modelId);
        }
        e.stopPropagation();
      }}
      onMouseLeave={() => isComponent && setHoveredComponentId(null)}
      $hasContent={hasContent}
      $isRoot={isRoot}
      $showVertical={showVertical}
      onClick={e => {
        if (isComponent) {
          setFocusedComponentId(modelId);
          onComponentClick(modelId);
        }
        e.stopPropagation();
      }}
      onDoubleClick={e => {
        if (isComponent) {
          onComponentDoubleClick(modelId);
        }
        e.stopPropagation();
      }}
      ref={containerRef}
      {...(isCollapsedReference
        ? {
            [BULK_POPOVER_ATTR]: 'true',
            [VIEW_NODE_ID_ATTR]: node.id,
          }
        : { [BULK_POPOVER_ATTR]: 'true' })}
    >
      <ColorBox
        $degreeOfParenthood={degreeOfParenthood || degreeOfChildhood}
        $isCollapsedReference={isCollapsedReference}
        $isReference={isReference}
        $containsCollapsedNodes={containsCollapsedNodes}
        $isActive={isActive}
        $isSelected={isSelected}
        className={classes(
          'node',
          EXPORT_AUTOSIZE_CLASS_NAME,
          !isNewConditionalFormatting && className,
          isReference && 'reference'
        )}
        style={
          isNewConditionalFormatting
            ? { color: stroke, backgroundColor: fill }
            : undefined
        }
      />
      {headerLabel && (
        <DependencyMapReferenceLabel
          id={referenceDescId}
          $hasOnReferenceClick={Boolean(onReferenceClick)}
          $isWidthSmallerThanVisible={isWidthSmallerThanVisible}
          $textModerateColor={textModerateColor}
          label={headerLabel}
          className={classes('referenceLabel', EXPORT_AUTOSIZE_CLASS_NAME)}
          onClickCapture={e => {
            if (!onReferenceClick) return;
            const referenceId = referenceIds?.length
              ? referenceIds[0]
              : node.id;
            onReferenceClick(referenceId);
            e.stopPropagation();
          }}
        />
      )}
      {!!headerSubLabels?.length && (
        <OtherLabelsContainer>
          {headerSubLabels.map(([title, caption, showTitle]) => (
            <OtherLabel
              key={`${title}: ${caption}`}
              title={title}
              caption={caption}
              showTitle={showTitle}
              preset={isNewConditionalFormatting ? 'shadowed' : 'legacy'}
            />
          ))}
        </OtherLabelsContainer>
      )}
      <ComponentNameContainer
        id={labelId}
        className={EXPORT_AUTOSIZE_CLASS_NAME}
        $isWidthSmallerThanVisible={isWidthSmallerThanVisible}
        $textColor={textColor}
      >
        <ComponentLabel
          representationData={representationData}
          label={label}
          iconColor={iconColor}
          isWidthSmallerThanVisible={isWidthSmallerThanVisible}
        />
        {Boolean(urlFieldValues.length) && (
          <UnstyledButton
            type="button"
            {...{
              [BULK_POPOVER_ATTR]: 'true',
              [URL_FIELD_VALUES_ATTR]: 'true',
            }}
            onClick={e => {
              maybeDispatchUrlIconClick(
                urlFieldValues,
                ViewIds.DEPENDENCY_MAP_2
              );
              e.stopPropagation();
            }}
          >
            <UrlFieldValueIcon />
          </UnstyledButton>
        )}
      </ComponentNameContainer>
      {subLabel && (
        <SubLabel $textColor={textModerateColor}>{subLabel}</SubLabel>
      )}
      {otherLabels?.length ? (
        <OtherLabelsContainer>
          {otherLabels.map(([title, caption, showTitle]) => (
            <OtherLabel
              key={`${title}: ${caption}`}
              title={title}
              caption={caption}
              showTitle={showTitle}
              preset={isNewConditionalFormatting ? 'shadowed' : 'legacy'}
            />
          ))}
        </OtherLabelsContainer>
      ) : null}
      {hasContent && (
        <ItemsContainer
          $showVertical={showVertical}
          className={EXPORT_AUTOSIZE_CLASS_NAME}
        >
          <DependencyMap.ViewNodeChildren
            viewInstanceId={viewInstanceId}
            node={node}
            colorByReference={colorByReference}
            selectedComponentId={selectedComponentId}
            hoveredComponentId={hoveredComponentId}
            focusedComponentId={focusedComponentId}
            setHoveredComponentId={setHoveredComponentId}
            setFocusedComponentId={setFocusedComponentId}
            showVertical={showVertical}
            referenceParents={isReference}
            onComponentClick={onComponentClick}
            onComponentDoubleClick={onComponentDoubleClick}
            onReferenceClick={onReferenceClick}
            urlFieldValuesByComponentId={urlFieldValuesByComponentId}
            urlFieldValuesByReferenceId={urlFieldValuesByReferenceId}
            contextMenuDataAttribute={contextMenuDataAttribute}
            isNewConditionalFormatting={isNewConditionalFormatting}
            DependencyMap={DependencyMap}
          />
          <DependencyMap.ViewNodeReferences
            viewInstanceId={viewInstanceId}
            node={node}
            colorByReference={colorByReference}
            selectedComponentId={selectedComponentId}
            hoveredComponentId={hoveredComponentId}
            focusedComponentId={focusedComponentId}
            setHoveredComponentId={setHoveredComponentId}
            setFocusedComponentId={setFocusedComponentId}
            onComponentClick={onComponentClick}
            onReferenceClick={onReferenceClick}
            onComponentDoubleClick={onComponentDoubleClick}
            urlFieldValuesByComponentId={urlFieldValuesByComponentId}
            urlFieldValuesByReferenceId={urlFieldValuesByReferenceId}
            contextMenuDataAttribute={contextMenuDataAttribute}
            isNewConditionalFormatting={isNewConditionalFormatting}
            DependencyMap={DependencyMap}
          />
        </ItemsContainer>
      )}
    </Container>
  );
};

const isComponentIdPresentInNode = (
  node: DependencyMapNodeViewModel,
  componentId: string | null
) =>
  node.modelId === componentId ||
  nodeHasComponentIdInReferences(node, componentId) ||
  (node.children && hasComponentId(node.children, componentId));

const isViewNodeEffectivelyEqual = (
  previousProps: DependencyMapViewNodeProperties,
  nextProps: DependencyMapViewNodeProperties
): boolean => {
  if (
    previousProps.viewInstanceId !== nextProps.viewInstanceId ||
    previousProps.headerLabel !== nextProps.headerLabel ||
    !isEqual(previousProps.headerSubLabels, nextProps.headerSubLabels) ||
    previousProps.referenceDirection !== nextProps.referenceDirection ||
    previousProps.colorByReference !== nextProps.colorByReference ||
    previousProps.className !== nextProps.className ||
    previousProps.isRoot !== nextProps.isRoot ||
    previousProps.showVertical !== nextProps.showVertical ||
    !haveSameCallbacks(previousProps, nextProps) ||
    !isNodeEffectivelyEqual(previousProps.node, nextProps.node)
  ) {
    return false;
  }
  const { node } = nextProps;

  if (previousProps.hoveredComponentId !== nextProps.hoveredComponentId) {
    if (
      isComponentIdPresentInNode(node, previousProps.hoveredComponentId) ||
      isComponentIdPresentInNode(node, nextProps.hoveredComponentId)
    ) {
      return false;
    }
  }

  if (previousProps.focusedComponentId !== nextProps.focusedComponentId) {
    if (
      isComponentIdPresentInNode(node, previousProps.focusedComponentId) ||
      isComponentIdPresentInNode(node, nextProps.focusedComponentId)
    ) {
      return false;
    }
  }

  if (previousProps.selectedComponentId !== nextProps.selectedComponentId) {
    if (
      isComponentIdPresentInNode(node, previousProps.selectedComponentId) ||
      isComponentIdPresentInNode(node, nextProps.selectedComponentId)
    ) {
      return false;
    }
  }

  return true;
};

export const DependencyMapViewNode = memo(
  DependencyMapViewNodeBase,
  isViewNodeEffectivelyEqual
);
